import { useEffect, useState } from 'react';
import {
  CategoryPillRow,
  type HeaderCategory,
} from '../../../modules/design-system';
import { CategoryDropdownPill } from './CategoryDropdownPill';
import { MyPacksPill } from './MyPacksPill';

export type { HeaderCategory };

export interface HeaderProps {
  categories: HeaderCategory[];
  activeCategoryId?: string;
  onCategorySelect?: (id: string) => void;
  onMyPacksClick?: () => void;
  className?: string;
}

function useIsScrolled(threshold = 8): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const update = () => setScrolled(window.scrollY > threshold);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [threshold]);
  return scrolled;
}

/**
 * Modern header — Figma `543:6352` (desktop) / `581:564` (mobile).
 *
 *   < 600px → single CategoryDropdownPill (active category + chevron) on
 *             the left, glass MyPacksPill on the right.
 *   ≥ 600px → horizontal CategoryPillRow flush-left, same MyPacksPill on
 *             the right.
 *
 * No logo on either breakpoint. 20px vertical padding. On scroll the bar
 * darkens for legibility — same affordance as the classic header.
 */
export function Header({
  categories,
  activeCategoryId,
  onCategorySelect,
  onMyPacksClick,
  className = '',
}: HeaderProps) {
  const scrolled = useIsScrolled(8);

  return (
    <header
      data-scrolled={scrolled || undefined}
      data-co-component="Header"
      className={
        '@container sticky top-0 z-40 ' +
        'py-5 ' +
        'transition-[background-color,backdrop-filter,border-color] duration-200 ease-out ' +
        (scrolled
          ? 'bg-black/40 backdrop-blur-md border-b border-white/10 '
          : 'border-b border-transparent ') +
        className
      }
    >
      {/* Operator-theme tint layer — spans full header width so the
          scroll-darken effect (and any --co-Header-bg override) reaches
          the viewport edges, NOT just the content container. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-[1]"
        style={{ background: 'var(--co-Header-bg)', color: 'var(--co-Header-text)' }}
      />

      {/* Content container — mirrors PackLayout's inner wrapper so the
          header's category pills + MyPacks pin align with the slider's
          left edge and the sidebar's right edge across every viewport. */}
      <div
        className="
          mx-auto max-w-350
          flex items-center justify-between gap-3
          px-4 @[600px]:px-6 @[900px]:px-8
        "
      >
        {/* Mobile — single dropdown pill with listbox popover */}
        {categories.length > 0 && (
          <div className="@[600px]:hidden">
            <CategoryDropdownPill
              categories={categories}
              {...(activeCategoryId !== undefined ? { activeId: activeCategoryId } : {})}
              {...(onCategorySelect ? { onSelect: onCategorySelect } : {})}
            />
          </div>
        )}

        {/* Desktop — full pill row */}
        <div className="hidden min-w-0 flex-1 @[600px]:block">
          <CategoryPillRow
            categories={categories}
            {...(activeCategoryId !== undefined ? { activeId: activeCategoryId } : {})}
            {...(onCategorySelect ? { onSelect: onCategorySelect } : {})}
            centered={false}
          />
        </div>

        <MyPacksPill {...(onMyPacksClick ? { onClick: onMyPacksClick } : {})} />
      </div>
    </header>
  );
}
